// src/utils/weldSchoolDistricts.js
// Weld County school board district lookup: Turf.js point-in-polygon against
// the School_Districts GeoJSON, translated to the Airtable Officials base's
// `District` field values so school board officials can be looked up.

import { point as turfPoint, booleanPointInPolygon } from '@turf/turf';

export const WELD_SCHOOL_DISTRICTS_FILE = '/geojson/weld/School_Districts_open_data_3863482203325707312.geojson';

// GeoJSON `DISTRICT` property -> Airtable Officials `District` field value
const WELD_SCHOOL_DISTRICT_TO_AIRTABLE = {
  '6': 'Greeley-Evans SD 6',
  'RE-4': 'Weld RE-4',
  'RE-2': 'Weld RE-2',
  'RE-5J': 'Weld RE-5J',
  'RE-1': 'Weld RE-1',
  'RE-3J': 'Weld RE-3J',
  'RE-9': 'Weld RE-9',
  'RE-10': 'Weld RE-10',
  'RE-11': 'Weld RE-11',
  'RE-12': 'Weld RE-12',
  'RE-7': 'Weld RE-7',
  'RE-20J': 'Weld RE-20J',
  '50J': 'Weld RE-50J',
  'RE-8': 'Weld RE-8',
};

export const mapWeldSchoolDistrictToAirtable = (districtCode) =>
  WELD_SCHOOL_DISTRICT_TO_AIRTABLE[districtCode] || null;

// GeoJSON `DISTRICT` property -> friendly, human-readable display name.
// Kept separate from the Airtable mapping above, which must stay in
// Airtable's exact `District` field format for officials matching to work.
const WELD_SCHOOL_DISTRICT_FRIENDLY_NAMES = {
  '6': 'Greeley-Evans SD 6',
  'RE-4': 'Weld RE-4 (Windsor)',
  'RE-2': 'Weld RE-2 (Eaton)',
  'RE-5J': 'Weld RE-5J (Johnstown-Milliken)',
  'RE-1': 'Weld RE-1 (Valley)',
  'RE-3J': 'Weld RE-3J (Weld Central)',
  'RE-9': 'Weld RE-9 (Ault-Highland)',
  'RE-10': 'Weld RE-10 (Briggsdale)',
  'RE-11': 'Weld RE-11 (Prairie)',
  'RE-12': 'Weld RE-12 (Pawnee)',
  'RE-7': 'Weld RE-7 (Platte Valley)',
  'RE-20J': 'Weld RE-20J (Weldon Valley)',
  '50J': 'Weld RE-50J (Wiggins)',
  'RE-8': 'Weld RE-8 (Fort Lupton)',
  'R-2J': 'Thompson R-2J',
  'RE-1J': 'St. Vrain Valley RE-1J',
  '27J': 'Brighton 27J',
};

export const getWeldSchoolDistrictFriendlyName = (districtCode) =>
  WELD_SCHOOL_DISTRICT_FRIENDLY_NAMES[districtCode] || districtCode;

let schoolGeojsonCache = null;

const loadWeldSchoolGeojson = async () => {
  if (schoolGeojsonCache) return schoolGeojsonCache;
  const res = await fetch(WELD_SCHOOL_DISTRICTS_FILE);
  if (!res.ok) throw new Error(`Failed to load ${WELD_SCHOOL_DISTRICTS_FILE}: ${res.status}`);
  schoolGeojsonCache = await res.json();
  return schoolGeojsonCache;
};

// Returns the raw GeoJSON `DISTRICT` code (e.g. "RE-11") containing (lat, lng), or null.
export const lookupWeldSchoolDistrictCode = async (lat, lng) => {
  const geojson = await loadWeldSchoolGeojson();
  const pt = turfPoint([lng, lat]);
  const feature = geojson.features.find(f => booleanPointInPolygon(pt, f));
  return feature?.properties?.DISTRICT || null;
};
